import * as Dialog from "@radix-ui/react-dialog";
import CloseIcon from "assets/img/icons/header/close.svg?react";
import {
    Button,
    Heading,
    Separator,
    SwitchInput,
    TablePagination,
    TableSearch,
} from "components/elements";
import { DocumentsTable } from "components/layout/documents/DocumentsTable";
import { useFileList } from "hooks/useFileList";
import { ReactNode, useMemo, useState } from "react";
import * as React from "react";
import { ClientDocument, ClientDocumentListEntry } from "@compass/shared/types";

type VisibilityFilter = "all" | "private" | "public";

export const ManageAttachmentsModal = ({
    button,
    onAttach,
    isOpen,
    setIsOpen,
    blur,
    showPublicFiles = true,
    showPrivateFiles = false,
    alreadyAttachedIds,
}: {
    button: ReactNode | null;
    onAttach: (files: ClientDocument[]) => void;
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    blur?: boolean;
    showPublicFiles?: boolean;
    showPrivateFiles?: boolean;
    /** IDs of files already attached — shown as pre-selected */
    alreadyAttachedIds?: Set<string>;
}) => {
    const [page, setPage] = useState<number | undefined>(undefined);
    const [search, setSearch] = useState("");
    const [onlyMyFiles, setOnlyMyFiles] = useState(true);
    const [visibilityFilter, setVisibilityFilter] = useState<VisibilityFilter>(() => {
        if (showPublicFiles && showPrivateFiles) return "all";
        if (showPublicFiles) return "public";
        return "private";
    });

    // Track selections across filter/page changes
    const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set(alreadyAttachedIds));
    // Keep a map of id → doc so we can return full objects even after filtering away
    const [selectedDocsMap, setSelectedDocsMap] = useState<Map<string, ClientDocumentListEntry>>(
        () => new Map(),
    );

    const toggleSelection = (doc: ClientDocumentListEntry) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(doc.id)) {
                next.delete(doc.id);
            } else {
                next.add(doc.id);
            }
            return next;
        });
        setSelectedDocsMap((prev) => {
            const next = new Map(prev);
            if (next.has(doc.id)) {
                next.delete(doc.id);
            } else {
                next.set(doc.id, doc);
            }
            return next;
        });
    };

    const handleAttach = () => {
        const docs = Array.from(selectedIds)
            .map((id) => selectedDocsMap.get(id))
            .filter((d): d is ClientDocumentListEntry => d != null);
        onAttach(docs);
    };

    const newSelectionCount = useMemo(() => {
        if (!alreadyAttachedIds) return selectedIds.size;
        return [...selectedIds].filter((id) => !alreadyAttachedIds.has(id)).length;
    }, [selectedIds, alreadyAttachedIds]);

    const { data } = useFileList({
        search: search || undefined,
        page,
        showPublicFiles: visibilityFilter === "all" || visibilityFilter === "public",
        showPrivateFiles: visibilityFilter === "all" || visibilityFilter === "private",
        showOnlyMyFiles: onlyMyFiles || undefined,
    });

    return (
        <Dialog.Root
            open={isOpen}
            onOpenChange={(opened) => {
                setIsOpen(opened);
            }}
        >
            {button !== null && <Dialog.Trigger asChild>{button}</Dialog.Trigger>}
            <Dialog.Portal>
                <Dialog.Overlay
                    className={`fixed inset-0 z-50 bg-[#424242]/75 ${blur ? "backdrop-blur-xs" : ""}`}
                />
                <Dialog.Content className="fixed top-1/2 left-1/2 z-50 w-full max-w-fit -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white p-5">
                    <div className="flex flex-row">
                        <Dialog.Title asChild>
                            <Heading className="text-left align-middle font-sans text-[24px] leading-[100%] font-bold tracking-normal text-[#424242]">
                                Select Previous Attachments
                            </Heading>
                        </Dialog.Title>
                        <Dialog.Close asChild>
                            <button className="ml-auto" aria-label="Close">
                                <CloseIcon className="-mt-2 h-[30px] w-[30px]" />
                            </button>
                        </Dialog.Close>
                    </div>

                    <Separator className="mx-[-20px] my-4 h-[0.5px] bg-[#c8c8c8]" />

                    <div className="mb-3 flex flex-wrap items-center gap-3">
                        <TableSearch value={search} onChange={setSearch} />

                        <select
                            className="h-9 rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-700"
                            value={visibilityFilter}
                            onChange={(e) => {
                                setVisibilityFilter(e.target.value as VisibilityFilter);
                                setPage(undefined);
                            }}
                        >
                            <option value="all">All Files</option>
                            <option value="private">Private Only</option>
                            <option value="public">Public Only</option>
                        </select>

                        <SwitchInput
                            name="onlyMyFiles"
                            label="My files only"
                            value={onlyMyFiles}
                            onChange={(val) => {
                                setOnlyMyFiles(val);
                                setPage(undefined);
                            }}
                        />
                    </div>

                    <div className="flex">
                        <div className="flex w-full flex-col">
                            {data && (
                                <DocumentsTable
                                    documents={data?.documents || []}
                                    selectedIds={selectedIds}
                                    onToggleSelect={toggleSelection}
                                />
                            )}
                            {data && (
                                <TablePagination
                                    total={data?.totalFound}
                                    page={page}
                                    onChangePage={(page) => setPage(page)}
                                />
                            )}
                        </div>
                    </div>

                    {selectedIds.size > 0 && (
                        <div className="mt-4 flex justify-end gap-2">
                            <Button variant="primary" onClick={handleAttach}>
                                {selectedIds.size === 1 ? "Attach file" : "Attach files"}
                            </Button>
                            <Button
                                variant="secondary"
                                onClick={() => {
                                    setSelectedIds(new Set(alreadyAttachedIds));
                                    setSelectedDocsMap(new Map());
                                }}
                            >
                                Clear
                            </Button>
                        </div>
                    )}
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
};
